import { Suspense, useRef, useMemo } from "react";
import { Canvas, useFrame, useLoader } from "@react-three/fiber";
import { Environment, Float } from "@react-three/drei";
import * as THREE from "three";

const BOX_IMG = "/assets/lifewave_box.png";

function BoxPlane({ rotationOffset = 0, position = [0, 0, 0], speed = 0.6, scale = 1 }) {
  const ref = useRef();
  const texture = useLoader(THREE.TextureLoader, BOX_IMG);

  useMemo(() => {
    texture.anisotropy = 8;
    texture.colorSpace = THREE.SRGBColorSpace;
  }, [texture]);

  useFrame((_, delta) => {
    if (ref.current) ref.current.rotation.y += delta * speed;
  });

  return (
    <Float speed={1.4} rotationIntensity={0.1} floatIntensity={0.6}>
      <group position={position} rotation={[0, rotationOffset, 0]}>
        <group ref={ref}>
          {/* Front face */}
          <mesh>
            <planeGeometry args={[3.6 * scale, 1.9 * scale]} />
            <meshStandardMaterial
              map={texture}
              transparent
              side={THREE.DoubleSide}
              roughness={0.4}
              metalness={0.1}
              emissive="#00F0FF"
              emissiveIntensity={0.08}
            />
          </mesh>
          {/* Soft edge glow */}
          <mesh position={[0, 0, -0.02]}>
            <planeGeometry args={[3.9 * scale, 2.2 * scale]} />
            <meshBasicMaterial color="#00F0FF" transparent opacity={0.14} />
          </mesh>
        </group>
      </group>
    </Float>
  );
}

function StagePedestal() {
  return (
    <group position={[0, -1.6, 0]}>
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <ringGeometry args={[1.8, 2.1, 64]} />
        <meshBasicMaterial color="#00F0FF" transparent opacity={0.5} side={THREE.DoubleSide} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, 0]} position={[0, -0.02, 0]}>
        <ringGeometry args={[2.2, 2.7, 64]} />
        <meshBasicMaterial color="#D900FF" transparent opacity={0.25} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

export default function BoxShowcase360() {
  return (
    <Canvas camera={{ position: [0, 0.4, 6], fov: 45 }} dpr={[1, 1.8]} gl={{ alpha: true }}>
      <ambientLight intensity={0.5} />
      <pointLight position={[3, 3, 4]} color="#00F0FF" intensity={3} />
      <pointLight position={[-3, -2, 3]} color="#D900FF" intensity={2} />
      <pointLight position={[0, 4, -3]} color="#00FF66" intensity={1} />
      <Suspense fallback={null}>
        <Environment preset="city" />
        {/* Center large box */}
        <BoxPlane position={[0, 0.2, 0]} speed={0.7} scale={1.1} />
        {/* Flanking smaller boxes */}
        <BoxPlane position={[-3.2, -0.2, -1.5]} rotationOffset={Math.PI / 4} speed={-0.55} scale={0.7} />
        <BoxPlane position={[3.2, -0.2, -1.5]} rotationOffset={-Math.PI / 4} speed={0.55} scale={0.7} />
        <StagePedestal />
      </Suspense>
    </Canvas>
  );
}
